import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

const { FiPlus, FiCalendar, FiMapPin, FiUsers, FiShare2, FiEdit2, FiTrash2, FiGift, FiCoffee, FiCamera, FiExternalLink, FiX, FiCheck, FiMail, FiMessageSquare, FiArchive, FiRotateCcw } = FiIcons;

export default function PartyPlanner() {
  const { state } = useFamilyContext();
  const { user } = useAuth();
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    fetchParties();
  }, [showArchived]);

  const fetchParties = async () => {
    try {
      let query = supabase
        .from('parties')
        .select('*, party_guests(count)')
        .order('date', { ascending: true });
      
      if (showArchived) {
         query = query.eq('is_archived', true);
      } else {
         query = query.eq('is_archived', false); // Default: hide archived
      }

      const { data, error } = await query;

      if (error) throw error;
      setParties(data || []);
    } catch (error) {
      console.error('Error fetching parties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (confirm('Event wirklich unwiderruflich löschen?')) {
      await supabase.from('parties').delete().eq('id', id);
      fetchParties();
    }
  };

  const handleArchive = async (id, archiveStatus) => {
      await supabase.from('parties').update({ is_archived: archiveStatus }).eq('id', id);
      fetchParties();
  };

  const copyShareLink = (id) => {
    const link = `${window.location.origin}/#/party/${id}`;
    navigator.clipboard.writeText(link);
    alert('Einladungslink kopiert!');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Feierplaner</h1>
          <p className="text-gray-600 mt-2 dark:text-gray-400">Planen Sie unvergessliche Events</p>
        </div>
        <div className="flex space-x-3">
            <button
                onClick={() => setShowArchived(!showArchived)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${showArchived ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'}`}
            >
                <SafeIcon icon={FiArchive} className="w-5 h-5 mr-2" />
                {showArchived ? 'Aktuelle Events' : 'Archiv'}
            </button>
            <button
            onClick={() => { setEditingParty(null); setShowModal(true); }}
            className="flex items-center px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
            >
            <SafeIcon icon={FiPlus} className="w-5 h-5 mr-2" />
            Neues Event
            </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Laden...</div>
      ) : parties.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm dark:bg-gray-800 dark:border-gray-700">
          <SafeIcon icon={FiGift} className="w-16 h-16 text-pink-200 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">{showArchived ? 'Keine archivierten Events' : 'Keine Events geplant'}</h3>
          {!showArchived && <p className="text-gray-500 mt-2 dark:text-gray-400">Starten Sie jetzt mit Ihrer ersten Party!</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {parties.map((party) => (
            <motion.div
              key={party.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col dark:bg-gray-800 dark:border-gray-700 ${party.is_archived ? 'opacity-75 grayscale' : ''}`}
            >
              <div className="h-40 bg-gradient-to-r from-pink-500 to-purple-600 relative">
                {party.image_url && <img src={party.image_url} alt={party.title} className="w-full h-full object-cover opacity-80" />}
                <div className="absolute top-4 right-4 flex space-x-2">
                  {!party.is_archived && (
                      <button onClick={() => copyShareLink(party.id)} className="p-2 bg-white/90 rounded-full hover:bg-white text-gray-700 shadow-sm" title="Link kopieren">
                        <SafeIcon icon={FiShare2} className="w-4 h-4" />
                      </button>
                  )}
                  <button onClick={() => { setEditingParty(party); setShowModal(true); }} className="p-2 bg-white/90 rounded-full hover:bg-white text-blue-600 shadow-sm">
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                  
                  {party.is_archived ? (
                      <button onClick={() => handleArchive(party.id, false)} className="p-2 bg-white/90 rounded-full hover:bg-white text-green-600 shadow-sm" title="Wiederherstellen">
                        <SafeIcon icon={FiRotateCcw} className="w-4 h-4" />
                      </button>
                  ) : (
                      <button onClick={() => handleArchive(party.id, true)} className="p-2 bg-white/90 rounded-full hover:bg-white text-orange-600 shadow-sm" title="Archivieren">
                        <SafeIcon icon={FiArchive} className="w-4 h-4" />
                      </button>
                  )}
                  
                  <button onClick={() => handleDelete(party.id)} className="p-2 bg-white/90 rounded-full hover:bg-white text-red-600 shadow-sm" title="Löschen">
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-xl font-bold text-gray-900 mb-2 dark:text-white">{party.title}</h3>
                
                <div className="space-y-2 text-sm text-gray-600 mb-4 dark:text-gray-400">
                  <div className="flex items-center">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-2 opacity-70" />
                    {format(new Date(party.date), 'dd. MMMM yyyy - HH:mm', { locale: de })}
                  </div>
                  {party.location && (
                    <div className="flex items-center">
                      <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-2 opacity-70" />
                      {party.location}
                    </div>
                  )}
                  <div className="flex items-center text-blue-600 dark:text-blue-400">
                     <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2 opacity-70" />
                     {party.party_guests?.length || 0} Gäste auf der Liste
                  </div>
                </div>

                <div className="mt-auto pt-4 border-t border-gray-100 flex justify-between items-center text-xs font-medium text-gray-500 dark:border-gray-700 dark:text-gray-400">
                   <div className="flex space-x-3">
                      {party.features?.buffet && <span className="flex items-center text-green-600 dark:text-green-400"><SafeIcon icon={FiCoffee} className="w-3 h-3 mr-1" /> Buffet</span>}
                      {party.features?.wishlist && <span className="flex items-center text-purple-600 dark:text-purple-400"><SafeIcon icon={FiGift} className="w-3 h-3 mr-1" /> Wünsche</span>}
                   </div>
                   <a href={`/#/party/${party.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:underline dark:text-blue-400">
                     Vorschau <SafeIcon icon={FiExternalLink} className="ml-1 w-3 h-3" />
                   </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showModal && (
        <PartyModal
          party={editingParty}
          onClose={() => setShowModal(false)}
          onSave={() => { fetchParties(); setShowModal(false); }}
        />
      )}
    </div>
  );
}

function PartyModal({ party, onClose, onSave }) {
  const { user, familyData } = useAuth();
  const [activeTab, setActiveTab] = useState('details'); // details, buffet, guests
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '18:00',
    location: '',
    description: '',
    features: { buffet: false, wishlist: false, photos: false },
    wishlist_link: '',
    image_url: ''
  });
  
  // Buffet State
  const [buffetItems, setBuffetItems] = useState([]);
  const [newItem, setNewItem] = useState('');

  // Guests State
  const [guestList, setGuestList] = useState([]);

  useEffect(() => {
    if (party) {
      setFormData({
        ...party,
        date: party.date ? party.date.split('T')[0] : '',
        time: party.date ? format(new Date(party.date), 'HH:mm') : '18:00',
        features: party.features || { buffet: false, wishlist: false, photos: false }
      });
      if (party.features?.buffet) {
          fetchBuffetItems(party.id);
      }
      fetchGuestList(party.id);
    }
  }, [party]);

  const fetchBuffetItems = async (partyId) => {
      const { data } = await supabase.from('party_items').select('*').eq('party_id', partyId).eq('type', 'buffet');
      setBuffetItems(data || []);
  };

  const fetchGuestList = async (partyId) => {
      const { data } = await supabase.from('party_guests').select('*').eq('party_id', partyId);
      setGuestList(data || []);
  };

  const handleAddBuffetItem = async () => {
      if (!newItem.trim() || !party) return;
      const { data, error } = await supabase.from('party_items').insert({
          party_id: party.id,
          type: 'buffet',
          name: newItem,
          is_request: true, // It's a wish from the host
          assigned_to: null
      }).select().single();
      
      if (!error && data) {
          setBuffetItems([...buffetItems, data]);
          setNewItem('');
      }
  };

  const handleDeleteItem = async (itemId) => {
      await supabase.from('party_items').delete().eq('id', itemId);
      setBuffetItems(buffetItems.filter(i => i.id !== itemId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        date: new Date(`${formData.date}T${formData.time}`).toISOString(),
        family_id: familyData.id,
        creator_id: user.id
      };
      delete payload.time;

      if (party) {
        await supabase.from('parties').update(payload).eq('id', party.id);
      } else {
        await supabase.from('parties').insert(payload);
      }
      onSave();
    } catch (error) {
      alert('Fehler: ' + error.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 dark:bg-gray-800 dark:text-white">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{party ? 'Event bearbeiten' : 'Neues Event'}</h2>
            <button onClick={onClose}><SafeIcon icon={FiX} className="w-6 h-6" /></button>
          </div>

          {/* Tabs */}
          {party && (
              <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
                  <button 
                    onClick={() => setActiveTab('details')}
                    className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'details' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`}
                  >Details</button>
                  {formData.features.buffet && (
                      <button 
                        onClick={() => setActiveTab('buffet')}
                        className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'buffet' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`}
                      >Buffet Wünsche</button>
                  )}
                  <button 
                    onClick={() => setActiveTab('guests')}
                    className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === 'guests' ? 'border-b-2 border-pink-600 text-pink-600' : 'text-gray-500'}`}
                  >Gästeliste ({guestList.reduce((acc, g) => acc + (g.count || 1), 0)})</button>
              </div>
          )}

          {activeTab === 'details' ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium mb-1">Titel *</label>
                        <input type="text" required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Ort</label>
                        <input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} className="w-full border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Datum *</label>
                        <input type="date" required value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} className="w-full border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Uhrzeit *</label>
                        <input type="time" required value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} className="w-full border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">Beschreibung</label>
                    <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows="3" className="w-full border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600" />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2">Features</label>
                    <div className="flex space-x-4">
                        <label className="flex items-center space-x-2 bg-gray-50 p-2 rounded border dark:bg-gray-700 dark:border-gray-600">
                            <input type="checkbox" checked={formData.features.buffet} onChange={e => setFormData({...formData, features: {...formData.features, buffet: e.target.checked}})} className="rounded text-pink-600" />
                            <span>Buffet</span>
                        </label>
                        <label className="flex items-center space-x-2 bg-gray-50 p-2 rounded border dark:bg-gray-700 dark:border-gray-600">
                            <input type="checkbox" checked={formData.features.wishlist} onChange={e => setFormData({...formData, features: {...formData.features, wishlist: e.target.checked}})} className="rounded text-pink-600" />
                            <span>Wunschliste</span>
                        </label>
                         <label className="flex items-center space-x-2 bg-gray-50 p-2 rounded border dark:bg-gray-700 dark:border-gray-600">
                            <input type="checkbox" checked={formData.features.photos} onChange={e => setFormData({...formData, features: {...formData.features, photos: e.target.checked}})} className="rounded text-pink-600" />
                            <span>Fotos</span>
                        </label>
                    </div>
                </div>

                {formData.features.wishlist && (
                    <div>
                        <label className="block text-sm font-medium mb-1">Amazon Wunschliste (URL)</label>
                        <input type="url" value={formData.wishlist_link} onChange={e => setFormData({...formData, wishlist_link: e.target.value})} className="w-full border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600" />
                    </div>
                )}
                
                {party && (
                    <div className="pt-2">
                        <a href={`/#/party/${party.id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                            <SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-1" />
                            Vorschau der Gast-Ansicht öffnen
                        </a>
                    </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg dark:bg-gray-700 dark:text-gray-300">Abbrechen</button>
                    <button type="submit" className="px-6 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700">Speichern</button>
                </div>
              </form>
          ) : activeTab === 'buffet' ? (
              <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800 dark:bg-blue-900/30 dark:text-blue-200">
                      Definieren Sie hier, was für das Buffet benötigt wird (z.B. "Kartoffelsalat", "Baguette"). 
                      Gäste können diese Dinge dann auswählen ("claimen") oder eigene Dinge hinzufügen.
                  </div>
                  
                  <div className="flex space-x-2">
                      <input 
                        type="text" 
                        placeholder="Was wird benötigt?" 
                        value={newItem}
                        onChange={e => setNewItem(e.target.value)}
                        className="flex-1 border rounded-lg p-2 dark:bg-gray-700 dark:border-gray-600"
                        onKeyDown={e => e.key === 'Enter' && handleAddBuffetItem()}
                      />
                      <button onClick={handleAddBuffetItem} className="bg-pink-600 text-white px-4 rounded-lg hover:bg-pink-700">Hinzufügen</button>
                  </div>

                  <div className="space-y-2 max-h-60 overflow-y-auto">
                      {buffetItems.filter(i => i.is_request).map(item => (
                          <div key={item.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg dark:bg-gray-700">
                              <div>
                                  <span className="font-medium">{item.name}</span>
                                  {item.assigned_to && <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Mitgebracht von: {item.assigned_to}</span>}
                              </div>
                              <button onClick={() => handleDeleteItem(item.id)} className="text-red-500 hover:text-red-700"><SafeIcon icon={FiTrash2} className="w-4 h-4" /></button>
                          </div>
                      ))}
                      {buffetItems.filter(i => i.is_request).length === 0 && <p className="text-gray-500 text-center py-4">Noch keine Wünsche eingetragen.</p>}
                  </div>
                  
                  <div className="pt-4 border-t">
                      <h4 className="font-bold mb-2 text-sm">Bereits von Gästen hinzugefügt:</h4>
                      {buffetItems.filter(i => !i.is_request).length > 0 ? (
                          <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400">
                              {buffetItems.filter(i => !i.is_request).map(item => (
                                  <li key={item.id}>{item.name} ({item.assigned_to})</li>
                              ))}
                          </ul>
                      ) : (
                          <p className="text-sm text-gray-400">Keine eigenen Beiträge der Gäste bisher.</p>
                      )}
                  </div>
              </div>
          ) : (
              // GUESTS TAB
              <div className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                      <h3 className="font-bold">Gästeliste</h3>
                      <button onClick={() => fetchGuestList(party.id)} className="text-blue-600 text-sm hover:underline"><SafeIcon icon={FiIcons.FiRefreshCcw} className="w-3 h-3 inline mr-1" />Aktualisieren</button>
                  </div>
                  
                  {guestList.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg dark:bg-gray-700/50">
                          Bisher keine Anmeldungen. Teilen Sie den Link!
                      </div>
                  ) : (
                      <div className="space-y-3 max-h-80 overflow-y-auto">
                          {guestList.map(guest => (
                              <div key={guest.id} className="bg-gray-50 p-3 rounded-lg border border-gray-100 flex justify-between items-start dark:bg-gray-700 dark:border-gray-600">
                                  <div>
                                      <div className="font-bold flex items-center">
                                          {guest.status === 'accepted' ? (
                                              <span className="text-green-500 mr-2"><SafeIcon icon={FiCheck} className="w-4 h-4" /></span>
                                          ) : (
                                              <span className="text-yellow-500 mr-2"><SafeIcon icon={FiIcons.FiHelpCircle} className="w-4 h-4" /></span>
                                          )}
                                          {guest.name}
                                          <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                                              {guest.count} Pers.
                                          </span>
                                      </div>
                                      {guest.message && (
                                          <div className="text-sm text-gray-600 mt-1 italic flex items-start dark:text-gray-300">
                                              <SafeIcon icon={FiMessageSquare} className="w-3 h-3 mr-1 mt-1 opacity-50" />
                                              "{guest.message}"
                                          </div>
                                      )}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                      {format(new Date(guest.created_at), 'dd.MM HH:mm')}
                                  </div>
                              </div>
                          ))}
                      </div>
                  )}
              </div>
          )}
        </div>
      </div>
    </div>
  );
}